import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSignal } from "@preact/signals-react";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useCallback, useRef } from "react";

function adjustColor(hex, percent) {
  // If no color is passed, return black
  if (!hex) {
    return "#000000";
  }

  let adjustmentFactor = 1;
  let isWhite = hex === "#ffffff";

  // If color is black, set a higher brightening factor
  if (hex === "#000000") {
    hex = "#050505";
    adjustmentFactor = 75;
  }

  // Convert hex to rgb
  let [r, g, b] = hex
    .replace(/^#/, "")
    .match(/.{2}/g)
    .map((x) => parseInt(x, 16));

  // Calculate new color values
  if (isWhite) {
    // For white, calculate darkening values
    r = Math.round(r - (r * percent) / 100);
    g = Math.round(g - (g * percent) / 100);
    b = Math.round(b - (b * percent) / 100);
  } else {
    // For non-white, calculate brightening values
    r = Math.round((r * (100 + adjustmentFactor * percent)) / 100);
    g = Math.round((g * (100 + adjustmentFactor * percent)) / 100);
    b = Math.round((b * (100 + adjustmentFactor * percent)) / 100);
  }

  // Ensure values are within 0-255 range
  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  // Convert rgb back to hex
  const newHex = ((r << 16) | (g << 8) | b).toString(16);

  // Pad with leading zeros if necessary and return
  return "#" + ("000000" + newHex).slice(-6);
}

export const ColorChangingIcon = ({
  icon,
  hoverIcon,
  initialPrimaryColor,
  initialSecondaryColor = adjustColor(initialPrimaryColor, 20),
  hoverPrimaryColor,
  hoverSecondaryColor,
  clickPrimaryColor,
  clickSecondaryColor,
  primaryOpacity = 1,
  secondaryOpacity = 1,
  initialSize = 24,
  hoverSize = 24,
  onClick,
  float,
  tooltip = "",
  tooltipPosition = "top",
  disabled = false,
  ...props
}) => {
  const primaryColor = useSignal(initialPrimaryColor);
  const secondaryColor = useSignal(initialSecondaryColor);
  const fontSize = useSignal(initialSize);
  const isHovered = useRef(false);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    if (!hoverPrimaryColor) {
      primaryColor.value = adjustColor(primaryColor.value, 30);
    } else {
      primaryColor.value = hoverPrimaryColor;
    }
    if (!hoverSecondaryColor) {
      secondaryColor.value = adjustColor(secondaryColor.value, 30);
    } else {
      secondaryColor.value = hoverSecondaryColor;
    }
    fontSize.value = hoverSize;
    isHovered.current = true;
  }, [
    disabled,
    primaryColor,
    secondaryColor,
    hoverPrimaryColor,
    hoverSecondaryColor,
    hoverSize,
    icon,
    hoverIcon,
  ]);

  const handleMouseLeave = useCallback(() => {
    if (disabled) return;
    primaryColor.value = initialPrimaryColor;
    secondaryColor.value = initialSecondaryColor;
    fontSize.value = initialSize;
    isHovered.current = false;
  }, [disabled, initialPrimaryColor, initialSecondaryColor, initialSize, icon]);

  const handleClick = useCallback(
    (e) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      let primaryClickedColor;
      let secondaryClickedColor;
      if (!clickPrimaryColor) {
        primaryClickedColor = adjustColor(primaryColor.value, 50);
      } else {
        primaryClickedColor = clickPrimaryColor;
      }
      if (!clickSecondaryColor) {
        secondaryClickedColor = adjustColor(secondaryColor.value, 50);
      } else {
        secondaryClickedColor = clickSecondaryColor;
      }
      primaryColor.value = primaryClickedColor;
      secondaryColor.value = secondaryClickedColor;
      setTimeout(() => {
        let newPrimaryColor;

        if (isHovered.current) {
          newPrimaryColor = hoverPrimaryColor || adjustColor(initialPrimaryColor, 30);
        } else {
          newPrimaryColor = initialPrimaryColor;
        }

        primaryColor.value = newPrimaryColor;
      }, 200);

      setTimeout(() => {
        let newSecondaryColor;

        if (isHovered.current) {
          newSecondaryColor = hoverSecondaryColor || adjustColor(initialSecondaryColor, 30);
        } else {
          newSecondaryColor = initialSecondaryColor;
        }

        secondaryColor.value = newSecondaryColor;
      }, 200);

      onClick?.(e);
    },
    [
      disabled,
      initialPrimaryColor,
      initialSecondaryColor,
      primaryColor.value,
      secondaryColor.value,
      onClick,
    ]
  );

  return (
    <Tooltip
      openDelay={1000}
      anchorElement="target"
      parentTitle={true}
      position={tooltipPosition}
    >
      <FontAwesomeIcon
        title={tooltip}
        icon={isHovered.current ? hoverIcon || icon : icon}
        style={{
          color: primaryColor.value,
          fontSize: fontSize.value,
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "color 0.5s ease, font-size 0.5s ease, opacity 0.3s ease",
          margin: props.margin,
          padding: props.padding,
          position: props.position,
          transform: props.transform,
          zIndex: props.zIndex,
          "--fa-primary-opacity": disabled ? primaryOpacity * 0.4 : primaryOpacity,
          "--fa-secondary-opacity": disabled ? secondaryOpacity * 0.4 : secondaryOpacity,
          "--fa-primary-color": primaryColor.value || "#bbb",
          "--fa-secondary-color": secondaryColor.value || "#ddd",
          float: float,
          opacity: disabled ? 0.4 : 1,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        {...props}
      />
    </Tooltip>
  );
};

export default ColorChangingIcon;
